import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import ConfettiEffect from '../components/ConfettiEffect';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { saveUserEntry } from '../utils/db';
import { 
  playCorrectChime, 
  playWinFanfare, 
  playTickSound 
} from '../utils/soundEffects';
import { useGameExitRoute } from '../utils/navigation';

const { FiAward, FiSun, FiMoon, FiHome, FiRotateCcw, FiUser, FiArrowRight, FiActivity, FiZap } = FiIcons;

const CELESTIAL_WORLDS = [
  { id: 'earth', name: 'Earth', emoji: '🌍', gravityFactor: 1.0, jumpHeightMeters: 0.5, desc: 'Our home planet with standard 1G gravity.' },
  { id: 'moon', name: 'The Moon', emoji: '🌕', gravityFactor: 0.166, jumpHeightMeters: 3.0, desc: '1/6th gravity! You can float and leap over houses.' },
  { id: 'mars', name: 'Mars', emoji: '🔴', gravityFactor: 0.38, jumpHeightMeters: 1.3, desc: 'Red planet gravity is about 1/3rd of Earth.' },
  { id: 'jupiter', name: 'Jupiter', emoji: '🪐', gravityFactor: 2.53, jumpHeightMeters: 0.2, desc: 'Crushing gravity! You feel 2.5 times heavier.' },
  { id: 'sun', name: 'The Sun', emoji: '☀️', gravityFactor: 28.0, jumpHeightMeters: 0.02, desc: 'Intense gravity! A 30kg kid weighs 840 kg!' },
  { id: 'pluto', name: 'Pluto', emoji: '❄️', gravityFactor: 0.063, jumpHeightMeters: 7.9, desc: 'Tiny dwarf world! A gentle push launches you high into space!' }
];

const FUN_ITEMS = [
  { name: 'My Weight', baseWeight: 30, icon: '🧒' },
  { name: 'School Bag', baseWeight: 6, icon: '🎒' },
  { name: 'Cricket Bat', baseWeight: 1.2, icon: '🏏' },
  { name: 'Baby Elephant', baseWeight: 120, icon: '🐘' }
];

const CosmicGravityFlow = () => {
  const { exitRoute, exitTitle } = useGameExitRoute();
  const [step, setStep] = useState('lab'); // 'lab' | 'cert'
  const [kidName, setKidName] = useState('');
  const [selectedWorld, setSelectedWorld] = useState(CELESTIAL_WORLDS[1]); // Moon default
  const [selectedItem, setSelectedItem] = useState(FUN_ITEMS[0]);
  const [customWeight, setCustomWeight] = useState(30);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [couponCode, setCouponCode] = useState('');

  const activeWeight = selectedItem.name === 'My Weight' ? customWeight : selectedItem.baseWeight;
  const calculatedWeight = (activeWeight * selectedWorld.gravityFactor).toFixed(1);
  const jumpHeight = (0.5 / selectedWorld.gravityFactor).toFixed(1);

  const handleClaimCertificate = (e) => {
    e.preventDefault();
    if (!kidName.trim()) {
      alert("Please enter the student's name!");
      return;
    }
    const code = 'GRAV-' + Math.random().toString(36).substring(2, 7).toUpperCase();
    setCouponCode(code);

    saveUserEntry({
      name: kidName.trim(),
      mobile: 'STEM Lab Student',
      classGrade: 'Junior Physics',
      reward: `⭐ Junior Astrophysicist (${selectedWorld.name} Explorer)`,
      couponCode: code,
      gameType: 'Cosmic Gravity Lab'
    });

    setStep('cert');
    playCorrectChime();
    setTimeout(() => playWinFanfare(), 400);
  };

  const handleReset = () => {
    setKidName('');
    setStep('lab');
  };

  return (
    <div className={`min-h-screen transition-colors duration-500 flex flex-col justify-between p-4 md:p-6 select-none ${
      isDarkMode 
        ? 'bg-slate-950 text-slate-100' 
        : 'bg-gradient-to-br from-amber-50 via-sky-50 to-slate-100 text-slate-800'
    }`}>
      {step === 'cert' && <ConfettiEffect />}

      {/* Header */}
      <header className={`flex items-center justify-between pb-3 max-w-5xl mx-auto w-full border-b transition-colors ${
        isDarkMode ? 'border-slate-800' : 'border-slate-300 bg-white/70 backdrop-blur-md rounded-2xl px-4 shadow-sm'
      }`}>
        <div className="flex items-center gap-3">
          <Link to={exitRoute} title={exitTitle} className={`p-2.5 rounded-full border transition-all shadow-sm ${
            isDarkMode ? 'border-slate-700 bg-slate-900 text-slate-300 hover:text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100'
          }`}>
            <SafeIcon icon={FiHome} className="text-xl" />
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-2xl">⚖️</span>
              <h1 className="text-xl md:text-2xl font-black bg-clip-text text-transparent bg-gradient-to-r from-amber-500 via-orange-500 to-cyan-600 dark:from-amber-400 dark:via-orange-300 dark:to-cyan-400">
                Cosmic Gravity & Weight Explorer
              </h1>
            </div>
            <p className={`text-[11px] font-bold uppercase tracking-wider ${
              isDarkMode ? 'text-amber-400' : 'text-slate-600'
            }`}>
              Interactive Physics Lab • How Much Do You Weigh Across the Solar System?
            </p>
          </div>
        </div>

        <button 
          onClick={() => setIsDarkMode(!isDarkMode)}
          className={`px-3 py-1.5 rounded-full text-xs font-bold border flex items-center gap-1.5 cursor-pointer shadow-md transition-all ${
            isDarkMode ? 'border-slate-700 bg-slate-900 text-amber-300' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
          }`}
        >
          <SafeIcon icon={isDarkMode ? FiSun : FiMoon} />
          <span>{isDarkMode ? 'Night Mode 🌌' : 'Day Mode ☀️'}</span>
        </button>
      </header>

      {/* Main Lab Screen */}
      <main className="flex-1 flex flex-col items-center justify-center my-6 max-w-4xl mx-auto w-full">
        <AnimatePresence mode="wait">
          {step === 'lab' && (
            <motion.div
              key="lab"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full space-y-6"
            >
              {/* Planetary Selector */}
              <div className={`p-4 sm:p-6 rounded-3xl border shadow-xl transition-all ${
                isDarkMode ? 'bg-slate-900/90 border-slate-700 text-white' : 'bg-white border-slate-300 shadow-xl text-slate-900'
              }`}>
                <span className={`text-xs font-bold uppercase tracking-wider block mb-3 ${
                  isDarkMode ? 'text-slate-400' : 'text-slate-600'
                }`}>
                  1. Select a Celestial Destination:
                </span>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-3">
                  {CELESTIAL_WORLDS.map(world => (
                    <button
                      key={world.id}
                      onClick={() => {
                        setSelectedWorld(world);
                        playTickSound(750, 0.05);
                      }}
                      className={`p-3 rounded-2xl border-2 flex flex-col items-center transition-all cursor-pointer ${
                        selectedWorld.id === world.id
                          ? isDarkMode
                            ? 'bg-amber-500/20 border-amber-400 text-white shadow-lg shadow-amber-500/20 scale-105'
                            : 'bg-amber-100 border-amber-500 text-slate-900 shadow-md scale-105'
                          : isDarkMode
                            ? 'bg-slate-800/60 border-slate-700 text-slate-400 hover:border-slate-500'
                            : 'bg-slate-50 border-slate-200 text-slate-700 hover:border-slate-300'
                      }`}
                    >
                      <span className="text-3xl sm:text-4xl mb-1">{world.emoji}</span>
                      <span className="text-xs font-bold">{world.name}</span>
                      <span className={`text-[10px] mt-0.5 font-bold ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>{world.gravityFactor}g</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Weight Scale Display Panel */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                {/* Scale Measurement readout */}
                <div className={`p-6 rounded-3xl border shadow-2xl flex flex-col justify-between transition-all ${
                  isDarkMode 
                    ? 'bg-gradient-to-br from-slate-900 to-indigo-950/80 border-slate-700 text-white' 
                    : 'bg-white border-slate-300 shadow-xl text-slate-900'
                }`}>
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs font-black uppercase text-amber-500">
                        Cosmic Scale Telemetry
                      </span>
                      <span className={`text-xs font-bold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Gravity: {selectedWorld.gravityFactor} × Earth
                      </span>
                    </div>

                    <div className={`p-6 rounded-2xl border text-center my-2 transition-all ${
                      isDarkMode ? 'bg-slate-950/70 border-slate-800' : 'bg-amber-50/60 border-amber-200'
                    }`}>
                      <span className={`text-xs font-bold uppercase tracking-widest block mb-1 ${
                        isDarkMode ? 'text-slate-400' : 'text-slate-600'
                      }`}>
                        Weight on {selectedWorld.name}
                      </span>
                      <span className="text-4xl sm:text-5xl font-black font-mono text-amber-500">
                        {calculatedWeight} <span className={`text-xl font-sans ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>kg</span>
                      </span>
                      <div className={`text-xs mt-2 ${isDarkMode ? 'text-slate-500' : 'text-slate-500 font-medium'}`}>
                        (On Earth: {activeWeight} kg)
                      </div>
                    </div>

                    <div className={`mt-4 p-4 rounded-xl border text-xs leading-relaxed ${
                      isDarkMode ? 'bg-slate-800/50 border-slate-700/60 text-slate-300' : 'bg-slate-50 border-slate-200 text-slate-700'
                    }`}>
                      💡 <span className="font-bold">Physics Note:</span> {selectedWorld.desc}
                    </div>
                  </div>

                  {/* Jump Height Simulator */}
                  <div className={`mt-6 pt-4 border-t flex items-center justify-between ${
                    isDarkMode ? 'border-slate-800' : 'border-slate-200'
                  }`}>
                    <div>
                      <span className={`text-[10px] uppercase font-bold block ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Estimated High Jump</span>
                      <span className="text-xl font-black text-cyan-600 dark:text-cyan-400">{jumpHeight} meters!</span>
                    </div>
                    <span className="text-3xl animate-bounce">🦘</span>
                  </div>
                </div>

                {/* Input Controls & Certificate Pass Form */}
                <div className={`p-6 rounded-3xl border shadow-xl flex flex-col justify-between ${
                  isDarkMode
                    ? 'bg-slate-900/90 border-slate-700 shadow-2xl'
                    : 'bg-white/95 border-slate-200 shadow-xl shadow-slate-200/50'
                }`}>
                  <div>
                    <span className={`text-xs font-bold uppercase tracking-wider block mb-3 ${
                      isDarkMode ? 'text-slate-400' : 'text-slate-600'
                    }`}>
                      2. Choose Test Item & Mass:
                    </span>
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      {FUN_ITEMS.map((item, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            setSelectedItem(item);
                            playTickSound(850, 0.04);
                          }}
                          className={`p-3 rounded-xl border font-bold text-xs flex items-center gap-2 transition-all cursor-pointer ${
                            selectedItem.name === item.name
                              ? isDarkMode
                                ? 'bg-cyan-500/20 border-cyan-400 text-white'
                                : 'bg-amber-500/15 border-amber-400 text-amber-900'
                              : isDarkMode
                                ? 'bg-slate-800/60 border-slate-700 text-slate-400 hover:border-slate-600'
                                : 'bg-slate-100 border-slate-200 text-slate-700 hover:border-slate-300'
                          }`}
                        >
                          <span className="text-xl">{item.icon}</span>
                          <span>{item.name}</span>
                        </button>
                      ))}
                    </div>

                    {selectedItem.name === 'My Weight' && (
                      <div className="mb-4">
                        <label className={`text-xs font-bold block mb-1 ${
                          isDarkMode ? 'text-slate-400' : 'text-slate-600'
                        }`}>
                          Adjust Your Earth Weight: {customWeight} kg
                        </label>
                        <input
                          type="range"
                          min="15"
                          max="80"
                          value={customWeight}
                          onChange={e => setCustomWeight(Number(e.target.value))}
                          className="w-full accent-amber-500 cursor-pointer"
                        />
                      </div>
                    )}
                  </div>

                  {/* Claim Student Badge Form */}
                  <form onSubmit={handleClaimCertificate} className={`mt-4 pt-4 border-t space-y-3 ${
                    isDarkMode ? 'border-slate-800' : 'border-slate-200'
                  }`}>
                    <input
                      type="text"
                      required
                      value={kidName}
                      onChange={e => setKidName(e.target.value)}
                      placeholder="Student Name for Certificate"
                      className={`w-full px-4 py-2.5 rounded-xl border text-xs font-bold focus:outline-none focus:ring-2 focus:ring-amber-400 ${
                        isDarkMode
                          ? 'border-slate-700 bg-slate-800 text-white placeholder-slate-500'
                          : 'border-slate-300 bg-slate-50 text-slate-900 placeholder-slate-400 focus:bg-white'
                      }`}
                    />

                    <button
                      type="submit"
                      className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-slate-950 font-black text-xs uppercase tracking-wider shadow-lg shadow-amber-500/20 flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer"
                    >
                      <SafeIcon icon={FiAward} />
                      <span>Claim Junior Astrophysicist Badge</span>
                    </button>
                  </form>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 2: Certified Astrophysicist Award Pass */}
          {step === 'cert' && (
            <motion.div
              key="cert"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className={`w-full max-w-md p-8 rounded-3xl border-2 border-amber-400 text-center shadow-2xl ${
                isDarkMode ? 'bg-slate-900 text-white' : 'bg-white text-slate-900'
              }`}
            >
              <div className="w-20 h-20 rounded-full bg-amber-500/20 text-amber-500 flex items-center justify-center text-4xl mx-auto mb-3 shadow-xl">
                <SafeIcon icon={FiAward} />
              </div>

              <div className="text-xs font-black uppercase text-amber-500 tracking-widest mb-1">
                STEM Science Award
              </div>

              <h2 className="text-2xl sm:text-3xl font-black mb-1">
                {kidName}
              </h2>

              <p className={`text-xs mb-6 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                Mastered Planetary Gravity & Mass across the Solar System!
              </p>

              <div className={`p-4 rounded-2xl border mb-6 text-left space-y-2 ${
                isDarkMode
                  ? 'bg-slate-800/80 border-amber-500/40'
                  : 'bg-amber-50/70 border-amber-200'
              }`}>
                <div className="flex justify-between text-xs">
                  <span className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>Target Planet:</span>
                  <span className="font-bold">{selectedWorld.name} ({selectedWorld.emoji})</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>Award Title:</span>
                  <span className="text-amber-500 font-bold">Junior Astrophysicist</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>Voucher Code:</span>
                  <span className="text-cyan-600 dark:text-cyan-300 font-mono font-bold">{couponCode}</span>
                </div>
              </div>

              <button
                onClick={handleReset}
                className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-slate-950 font-black text-sm flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer"
              >
                <SafeIcon icon={FiRotateCcw} />
                <span>Explore Another World</span>
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      <footer className={`text-center text-xs py-2 border-t ${
        isDarkMode ? 'text-slate-500 border-slate-800' : 'text-slate-500 border-slate-200'
      }`}>
        GamifyCenter STEM Academy • Gravity, Mass & Astronomical Mechanics
      </footer>
    </div>
  );
};

export default CosmicGravityFlow;
